import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, ExternalLink, Calendar, AlertCircle, Zap } from 'lucide-react';
import { apiGet, apiPost } from '../../../../utils/api';
import SeatManagementPanel from '../../../common/SeatManagementPanel';

const STATUS_BADGES = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  trialing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  past_due: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  canceled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const ClientBillingPanel = ({ clientId }) => {
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'subscriptions'],
    queryFn: async () => {
      const res = await apiGet('/billing/subscriptions');
      return res.ok ? res.json() : { subscriptions: [], exempt: false };
    },
  });

  const allSubs = data?.subscriptions || [];
  const exempt = data?.exempt || false;

  // Filter to subscriptions for this specific client
  const clientSubs = allSubs.filter(s => s.org_subscription && s.client_name);
  // Also show individual subs owned by this user
  const individualSubs = allSubs.filter(s => !s.org_subscription);

  const handlePortal = async () => {
    try {
      setPortalLoading(true);
      setError(null);
      const res = await apiPost('/billing/portal', { billing_type: 'org', client_id: clientId });
      if (res.ok) {
        const result = await res.json();
        window.location.href = result.url;
      } else {
        const result = await res.json();
        setError(result.error || 'Failed to open billing portal');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (exempt) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
        <p className="text-green-700 dark:text-green-400 font-rasa">
          Your organization has full platform access. No subscription required.
        </p>
      </div>
    );
  }

  const hasSubs = clientSubs.length > 0;
  const hasPaidSub = clientSubs.some(s => s.source === 'stripe');

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Org subscriptions */}
      {hasSubs ? (
        <div className="space-y-4">
          <h3 className="text-sm font-rasa font-semibold text-gray-500 dark:text-gray-400">Organization Subscriptions</h3>
          {clientSubs.map(sub => (
            <div key={sub.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard size={18} className="text-blue-500" />
                    <h4 className="font-unna font-semibold dark:text-white">{sub.plan_name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-rasa ${STATUS_BADGES[sub.status] || 'bg-gray-100 text-gray-600'}`}>
                      {sub.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 font-rasa space-y-1">
                    <p>{sub.source === 'stripe' ? sub.billing_interval : sub.source?.replace('_', ' ')}</p>
                    {sub.current_period_end && (
                      <p className="flex items-center gap-1">
                        <Calendar size={14} /> Renews {formatDate(sub.current_period_end)}
                      </p>
                    )}
                    {sub.cancel_at && (
                      <p className="text-orange-500 flex items-center gap-1">
                        <AlertCircle size={14} /> Canceling on {formatDate(sub.cancel_at)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Seat management */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <SeatManagementPanel subscriptionId={sub.id} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
          <CreditCard size={40} className="mx-auto mb-3 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400 font-rasa mb-3">No active organization subscriptions</p>
          <button
            onClick={() => navigate('/dashboard/upgrade')}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-rasa"
          >
            <Zap size={14} /> Subscribe
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {hasPaidSub && (
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-rasa dark:text-gray-300 disabled:opacity-50"
          >
            <ExternalLink size={16} />
            {portalLoading ? 'Opening...' : 'Manage Payment & Invoices'}
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard/upgrade')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-rasa"
        >
          <Zap size={14} />
          {hasSubs ? 'Change Plan' : 'View Plans'}
        </button>
      </div>
    </div>
  );
};

export default ClientBillingPanel;
